import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CategoryIcon from '@mui/icons-material/Category';
import AttributionIcon from '@mui/icons-material/Attribution';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WebIcon from '@mui/icons-material/Web';

/**
 * Sidebar component with navigation links
 * @returns {JSX.Element} The Sidebar component
 */
const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);

  // Check if the user is an admin
  const isAdmin = user?.role === 'admin';

  // Check if the user is approved
  const isApproved = user?.status === 'approved';

  // Navigation items for regular users
  const userNavItems = [
    // Home link removed
    // {
    //   text: t("navigation.dashboard"),
    //   icon: <DashboardIcon />,
    //   path: "/dashboard",
    // },
    {
      text: t('navigation.profile'),
      icon: <PersonIcon />,
      path: '/profile',
    },
    {
      text: t('navigation.products'),
      icon: <ShoppingBagIcon />,
      path: '/products',
    },
    { text: t('navigation.orders'), icon: <ReceiptIcon />, path: '/orders' },
    {
      text: t('navigation.returns'),
      icon: <AssignmentReturnIcon />,
      path: '/returns',
    },
    {
      text: t('navigation.wallet'),
      icon: <AccountBalanceWalletIcon />,
      path: '/wallet',
    },
    {
      text: t('navigation.account'),
      icon: <AccountCircleIcon />,
      path: '/account',
    },
  ];

  // Navigation items for admin users
  const adminNavItems = [
    {
      text: t('navigation.dashboard'),
      icon: <DashboardIcon />,
      path: '/admin',
    },
    { text: t('navigation.users'), icon: <PeopleIcon />, path: '/admin/users' },
    {
      text: t('navigation.categories'),
      icon: <CategoryIcon />,
      path: '/admin/categories',
    },
    {
      text: t('navigation.attributes'),
      icon: <AttributionIcon />,
      path: '/admin/attributes',
    },
    {
      text: t('navigation.products'),
      icon: <ShoppingBagIcon />,
      path: '/admin/products',
    },
    // {
    //   text: t("navigation.pricing"),
    //   icon: <PriceChangeIcon />,
    //   path: "/admin/pricing",
    // },
    {
      text: t('navigation.coupons'),
      icon: <LocalOfferIcon />,
      path: '/admin/coupons',
    },
    {
      text: t('navigation.adminCouponUsages'),
      icon: <ReceiptLongIcon />,
      path: '/admin/coupon-usages',
    },
    {
      text: t('navigation.orders'),
      icon: <ReceiptIcon />,
      path: '/admin/orders',
    },
    {
      text: t('navigation.returns'),
      icon: <AssignmentReturnIcon />,
      path: '/admin/returns',
    },
    // {
    //   text: t("navigation.inventory"),
    //   icon: <InventoryIcon />,
    //   path: "/admin/inventory",
    // },
    {
      text: t('navigation.stockMovements'),
      icon: <StackedLineChartIcon />,
      path: '/admin/stock-movements',
    },
    {
      text: t('navigation.shippingMethods'),
      icon: <LocalShippingIcon />,
      path: '/admin/shipping',
    },
    {
      text: t('navigation.taxRate'),
      icon: <ReceiptIcon />,
      path: '/admin/tax',
    },
    {
      text: t('navigation.bankDetails'),
      icon: <AccountBalanceIcon />,
      path: '/admin/bank-details',
    },
    {
      text: t('navigation.siteImages'),
      icon: <WebIcon />,
      path: '/admin/site-images',
    },
  ];

  // Admin can also access product catalog
  const adminExtraItems = [
    // Home link removed
    {
      text: t('navigation.products'),
      icon: <ShoppingBagIcon />,
      path: '/products',
    },
  ];

  return (
    <div>
      <Toolbar
        sx={{
          borderBottom: 1,
          borderColor: 'primary.light',
          display: 'flex',
          alignItems: 'center',
          padding: '16px',
        }}
      >
        <Typography
          variant="h6"
          noWrap
          component="div"
          fontFamily="'Playfair Display', serif"
          fontWeight={600}
        >
          {isAdmin ? t('navigation.admin') : t('navigation.menu')}
        </Typography>
      </Toolbar>
      <Divider />

      {/* User navigation - only show if user is not admin */}
      {!isAdmin && (
        <List>
          {userNavItems.map((item) => (
            <ListItem
              key={item.text}
              disablePadding
            >
              <ListItemButton
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? 'white.main' : 'inherit',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    '& .MuiTypography-root': {
                      fontWeight: location.pathname === item.path ? 600 : 400,
                      color: location.pathname === item.path ? 'white.main' : 'inherit',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}

      {/* Admin navigation */}
      {isAdmin && (
        <>
          <List>
            {adminNavItems.map((item) => (
              <ListItem
                key={item.text}
                disablePadding
              >
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={location.pathname === item.path}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: location.pathname === item.path ? 'white.main' : 'inherit',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiTypography-root': {
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        color: location.pathname === item.path ? 'white.main' : 'inherit',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {/* Additional user items for admins */}
          <Divider />
          <Box sx={{ p: 2 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
            >
              {t('navigation.browse')}
            </Typography>
          </Box>
          <List>
            {adminExtraItems.map((item) => (
              <ListItem
                key={item.text}
                disablePadding
              >
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={location.pathname === item.path}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: location.pathname === item.path ? 'white.main' : 'inherit',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiTypography-root': {
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        color: location.pathname === item.path ? 'white.main' : 'inherit',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </div>
  );
};

export default Sidebar;
